import { IconStarFilled } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getTestimonialsByLanguage } from "@/content/testimonials";
import { stats } from "@/content/stats";
import { useTranslation } from "react-i18next";
import { PurchaseButton } from "@/components/common/PurchaseButton";

const Testimonials = ({ onPurchase }: { onPurchase?: (planId: string) => void }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  // @ts-expect-error - useTranslation type issue
  const { t, i18n } = useTranslation();

  // Get testimonials based on current language
  const testimonials = useMemo(() => getTestimonialsByLanguage(i18n.language), [i18n.language]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let interval: NodeJS.Timeout;
    let isHovered = false;
    let isUserInteracting = false;
    let isWaitingForReset = false;

    const scroll = () => {
      if (!isHovered && !isUserInteracting && !isWaitingForReset && !isDragging) {
        // If we're near the end (within one card width), prepare to loop
        if (scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth - 320) {
          if (!isWaitingForReset) {
            isWaitingForReset = true;
          }

          // Wait for 1 second and then smoothly reset to start
          setTimeout(() => {
            scrollContainer.scrollLeft = 0;
            isWaitingForReset = false;
          }, 1000);

        } else {
          scrollContainer.scrollLeft += 1;
        }
      }
    };

    const handleMouseEnter = () => {
      isHovered = true;
    };

    const handleMouseLeave = () => {
      isHovered = false;
    };

    // Desktop mouse dragging handlers
    const handleMouseDown = (e: MouseEvent) => {
      setIsDragging(true);
      isUserInteracting = true;
      setStartX(e.pageX - scrollContainer.offsetLeft);
      setScrollLeft(scrollContainer.scrollLeft);
      scrollContainer.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 1.5;
      scrollContainer.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      scrollContainer.style.cursor = 'grab';
      setTimeout(() => {
        isUserInteracting = false;
      }, 2000);
    };

    const handleMouseLeaveContainer = () => {
      setIsDragging(false);
      scrollContainer.style.cursor = 'grab';
      setTimeout(() => {
        isUserInteracting = false;
      }, 2000);
    };

    // Touch interaction handlers (for auto-scroll pausing only)
    const handleTouchStart = () => {
      isUserInteracting = true;
    };

    const handleTouchEnd = () => {
      setTimeout(() => {
        isUserInteracting = false;
      }, 2000);
    };

    // Event listeners
    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    // Desktop mouse dragging
    scrollContainer.addEventListener('mousedown', handleMouseDown);
    scrollContainer.addEventListener('mousemove', handleMouseMove);
    scrollContainer.addEventListener('mouseup', handleMouseUp);
    scrollContainer.addEventListener('mouseleave', handleMouseLeaveContainer);

    // Mobile touch (native scrolling + auto-scroll pause)
    scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollContainer.addEventListener('touchend', handleTouchEnd, { passive: true });

    interval = setInterval(scroll, 30);

    return () => {
      clearInterval(interval);
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      scrollContainer.removeEventListener('mousedown', handleMouseDown);
      scrollContainer.removeEventListener('mousemove', handleMouseMove);
      scrollContainer.removeEventListener('mouseup', handleMouseUp);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeaveContainer);
      scrollContainer.removeEventListener('touchstart', handleTouchStart);
      scrollContainer.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, startX, scrollLeft]);

  return (
    <section className="py-6 md:py-12 bg-slate-50 overflow-hidden w-screen lg:w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-0 md:mb-4">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 leading-snug md:leading-snug">
            {(t as any)('testimonials.title.line1')
              .replace('{{users}}', Math.floor(stats.users / 10000) + ' 萬')
              .replace('{{usersEn}}', (Math.floor(stats.users / 1000) * 1000 / 1000) + 'k+ ')}<br className="block md:hidden" />{(t as any)('testimonials.title.line2')}
          </h2>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Left gradient shadow */}
        <div className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, rgb(248 250 252) 0%, rgba(248, 250, 252, 0) 100%)'
          }}
        />

        <div
          ref={scrollRef}
          className="flex space-x-2.5 overflow-x-auto px-12 py-8 no-scrollbar select-none"
          style={{
            scrollBehavior: isDragging ? 'auto' : 'smooth',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            cursor: 'grab'
          }}
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-64 md:w-80 bg-white rounded-2xl px-7 py-7 shadow-none duration-300 border border-slate-200"
            >
              <div className="flex items-center mb-4 gap-1">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <IconStarFilled key={i} className="w-5 h-5 text-yellow-400" />
                ))}
              </div>

              <p className="text-slate-700 md:leading-relaxed leading-relaxed text-base md:text-lg">
                {testimonial.content}
              </p>

              <div className="font-semibold text-slate-900">{testimonial.name}</div>
            </div>
          ))}
        </div>

        {/* Right gradient shadow */}
        <div className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to left, rgb(248 250 252) 0%, rgba(248, 250, 252, 0) 100%)'
          }}
        />
      </div>

      <div className="max-w-lg mx-auto px-8 sm:px-6 lg:px-8 mb-4">
        <div className="text-center">
          <p className="text-base md:text-lg text-slate-600 mb-6">
            {(t as any)('testimonials.note')}
          </p>
        </div>
      </div>

      {onPurchase && <PurchaseButton onPurchase={onPurchase} />}
    </section>
  );
};

export default Testimonials;
